<script setup lang="ts">
import { mergeRegister } from "@lexical/utils";
import {
  $createParagraphNode,
  $createTextNode,
  $getNearestNodeFromDOMNode,
  $getRoot,
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  $isRootNode,
  COMMAND_PRIORITY_LOW,
  DROP_COMMAND,
  PASTE_COMMAND,
  type PasteCommandType,
} from "lexical";
import { useLexicalComposer } from "lexical-vue";
import { onMounted, onUnmounted } from "vue";

import { notify } from "~/components/notifications/notification";
import { NotificationType } from "~/shared/enums";
import { useDataStore } from "~/stores";
import { areFileTypesAllowed } from "~/utils/media";
import { getOrdersBetween } from "~/utils/orderManager";

import { $createAttachmentNode } from "../nodes/AttachmentNode";
import { isEmpty } from "../utils";

const props = defineProps<{
  emitOnly?: boolean;
  dataTypes?: string[];
  invalidTypesMessage?: string;
}>();

const emit = defineEmits<{
  addAttachments: [files: File[]];
}>();

const dataStore = useDataStore();
const editor = useLexicalComposer();

const filterFiles = (files: File[]): File[] => {
  if (!props.dataTypes?.length) {
    return files;
  }

  if (!areFileTypesAllowed(files, props.dataTypes)) {
    notify({
      message: props.invalidTypesMessage ?? "Some file types are not supported",
      type: NotificationType.ERROR,
    });
    return [];
  }

  return files;
};

const createAttachments = async (files: File[], target?: HTMLElement) => {
  const lastAttachment = [...dataStore._duidsToAttachments.values()].at(-1);
  const orders = getOrdersBetween(lastAttachment?.order, undefined, files.length);
  const attachmentCreates = Array.from(files).map((file, i) => ({ file, order: orders[i] }));

  const creates = await dataStore.createAttachments(attachmentCreates);
  editor.update(() => {
    const selection = $getSelection();
    const selectionNode = selection && $isRangeSelection(selection) ? selection.anchor.getNode() : null;
    const anchorNode = target ? $getNearestNodeFromDOMNode(target) : selectionNode;
    const targetNode = (!anchorNode || $isElementNode(anchorNode) ? anchorNode : anchorNode.getParent()) ?? $getRoot();
    const parentIsRoot = $isRootNode(targetNode);

    creates.forEach((create) => {
      const attachmentNode = $createAttachmentNode(
        create.attachment.duid,
        ["image", "video", "audio"].every((kind) => !create.attachment.kind.startsWith(kind))
      );
      if (parentIsRoot) {
        const paragraphNode = $createParagraphNode();
        paragraphNode.append(attachmentNode);
        targetNode.append(paragraphNode);
      } else {
        targetNode.append(attachmentNode);
      }
    });
    if (parentIsRoot) {
      const newNode = $createParagraphNode();
      targetNode.append(newNode);
      newNode.select();
    } else {
      const newNode = $createTextNode("");
      targetNode.append(newNode);
      newNode.select();
    }
    if (anchorNode && isEmpty(anchorNode)) {
      anchorNode.remove();
    }
  });

  emit("addAttachments", files);
};

const handleFiles = async (files: File[], target?: HTMLElement) => {
  const validFiles = filterFiles(files);
  if (validFiles.length === 0) {
    return;
  }

  if (props.emitOnly) {
    emit("addAttachments", validFiles);
    return;
  }

  await createAttachments(validFiles, target);
};

let unregisterListeners: () => void;

onMounted(() => {
  unregisterListeners = mergeRegister(
    editor.registerCommand<DragEvent>(
      DROP_COMMAND,
      (event) => {
        if (!event.dataTransfer || event.dataTransfer.files.length === 0) {
          return false;
        }
        event.preventDefault();
        event.stopPropagation();
        handleFiles(Array.from(event.dataTransfer.files), event.target as HTMLElement);
        return true;
      },
      COMMAND_PRIORITY_LOW
    ),
    editor.registerCommand<PasteCommandType>(
      PASTE_COMMAND,
      (event) => {
        const clipboardData = event instanceof ClipboardEvent ? event.clipboardData : null;
        if (!clipboardData || clipboardData.files.length === 0) {
          return false;
        }
        event.preventDefault();

        handleFiles(Array.from(clipboardData.files));
        return false;
      },
      COMMAND_PRIORITY_LOW
    )
  );
});

onUnmounted(() => {
  unregisterListeners?.();
});

const addAttachments = (files: File[]) =>
  editor.update(() => {
    handleFiles(files);
  });

const handleDrop = (event: DragEvent) =>
  editor.update(() => {
    handleFiles(Array.from(event.dataTransfer?.files ?? []), event.target as HTMLElement);
  });

defineExpose({
  addAttachments,
  handleDrop,
});
</script>

<template>
  <slot />
</template>
